<template>
  <div>
    <!-- Name -->
    <div class="row mr-header">
      <div class="col-2">
        <div class="label mt-2 mb-1">Parts needed for Aircraft</div>
        <v-select
          :options="aircrafts"
          label="registration"
          :clearable="false"
          :reduce="(aircraft) => aircraft.id"
          placeholder="Please select an Aircraft..."
          v-model="materialRequest.aircraft"
          :disabled="materialRequest.isCompleted || !userCanEdit || !materialRequest.isEditable"
          @input="saveChanges"
        ></v-select>
      </div>

      <div class="col-2">
        <div class="label mt-2 mb-1">
          At location
          <span v-if="stationInfo.sisPdfUrl">
            <a :href="stationInfo.sisPdfUrl" target="_blank">
              <i class="far fa-link"></i>
              SIS
            </a>
          </span>
        </div>
        <v-select
          :options="airports"
          label="iataCode"
          :clearable="false"
          :reduce="(airport) => airport.id"
          placeholder="Please select an Airport..."
          v-model="materialRequest.airport"
          :disabled="materialRequest.isCompleted || !userCanEdit || !materialRequest.isEditable"
          @input="saveChanges"
        ></v-select>
      </div>

      <!-- to-link -->
      <div class="col-3" v-if="materialRequest.technicalOccurrenceGuid">
        <div class="label mt-2 mb-1">Technical Occurrence</div>
        <button class="badgebutton active" @click="navigateTechnicalOccurrence">
          <i class="far fa-link"></i>
          Go to linked Technical Occurrence
        </button>
      </div>

      <!-- send-report -->
      <div class="col-5">
        <div class="label mt-2 mb-1">Management Report</div>

        <div v-if="!parts.length">
          <div class="mb-2">
            <i class="far fa-info-circle"></i>
            Management Report will be available after adding the first part
          </div>
        </div>

        <div class="report-schedule-info" v-if="parts.length">
          <div>
            <button v-if="parts.length" class="badgebutton active" @click.stop="previewEmail">
              <i class="far fa-paper-plane text-white" />
              Send Management Report
            </button>
          </div>
          <div>
            <div>
              <i class="far fa-info-circle"></i>
              Last sent {{ getFormattedTimeValue(materialRequest.managementReportLastSent) }}
            </div>
            <div v-if="!materialRequest.isCompleted">
              <i class="far fa-info-circle"></i>
              Next send {{ getFormattedTimeValue(materialRequest.managementReportNextSend) }}
            </div>
          </div>
          <div>
            <b-popover ref="reportPostponePopover" target="report-postpone" triggers="click blur">
              <div class="postpone-header mt-2 mb-1">Postpone Management Report</div>

              <div class="postpone-body align-items-center mt-2">
                <div class="pt-1">
                  <div class="">Postpone for</div>
                </div>
                <div class="">
                  <input type="number" class="form-control" v-model="postponeValue" />
                </div>
                <div class="">
                  <div class="">hrs</div>
                </div>
              </div>

              <div class="mt-3 postpone-actions">
                <div>
                  <button type="button" class="btn btn-block btn-sm btn-secondary" @click="cancelReportPostpone()">
                    <i class="far fa-times"></i>
                    Cancel
                  </button>
                </div>
                <div>
                  <button
                    type="button"
                    class="btn btn-block btn-sm btn-success"
                    :disabled="postponeValue === null"
                    @click="saveReportPostpone()"
                  >
                    <i class="far fa-save"></i>
                    Save changes
                  </button>
                </div>
              </div>
            </b-popover>

            <button v-if="!materialRequest.isCompleted" id="report-postpone" class="btn btn-block rounded-pill btn-primary btn-xs mt-1 pl-3 pr-3">Postpone</button>
          </div>
        </div>
      </div>
    </div>

    <hr />

    <div class="row">
      <div class="col-12">
        <div class="label mt-2 mb-1 d-flex justify-content-between align-items-center">
          <span>Parts / Overview</span>
        </div>

        <div class="alert alert-info" v-if="!parts.length">
          <i class="far fa-info-circle"></i>
          Please add parts to this material request to start
        </div>

        <grid
          v-if="parts.length"
          :data-items="result"
          :columns="columns"
          style="font-size: 0.9rem"
          :filterable="false"
          :cell-render="'cellTemplate'"
          class="mrgrid"
        >
          <template v-slot:cellTemplate="{ props }">
            <td v-if="props.field === 'pn'">
              {{ props.dataItem[props.field] }}
              <br />
              <small>{{ props.dataItem['altPn'] }}</small>
            </td>

            <td v-else-if="props.field === 'selected'">
              <div class="d-flex align-items-center">
                <input
                  type="checkbox"
                  class="form-check-inputs"
                  id="exampleCheck1"
                  :checked="selectedParts.indexOf(props.dataItem['id']) !== -1"
                  @input="toggleSelection(props.dataItem['id'])"
                />

                <i :id="'info' + props.dataItem['id']" v-if="props.dataItem['remark']" class="far fa-info-circle ml-3 cursor-pointer"></i>
                <b-tooltip :target="'info' + props.dataItem['id']">
                  <div style="white-space: pre-wrap">
                    {{ props.dataItem['remark'] }}
                  </div>
                </b-tooltip>
              </div>
            </td>

            <td v-else-if="props.field === 'ipc'">
              {{ props.dataItem[props.field] }}
              <br />
              <small>{{ props.dataItem['altIpc'] }}</small>
            </td>

            <td v-else-if="props.field === 'requestStatus'">
              <template v-for="requestStatus in props.dataItem['requestStatus']">
                <div :key="requestStatus.requestStatus.requestStatusEnum">
                  <div class="badge" :class="requestStatus.requestStatus.cssClass">
                    {{ requestStatus.count }} x {{ requestStatus.requestStatus.name }}
                  </div>
                </div>
              </template>
              <div v-if="!props.dataItem['requestStatus'].length">N/A</div>
            </td>

            <td v-else-if="props.field === 'orderStatus'">
              <template v-for="orderStatus in props.dataItem['orderStatus']">
                <div :key="orderStatus.orderStatus.orderStatusEnum">
                  <div class="badge" :class="orderStatus.orderStatus.cssClass">
                    {{ orderStatus.count }} x {{ orderStatus.orderStatus.name }}
                  </div>
                </div>
              </template>
              <div v-if="!props.dataItem['orderStatus'].length">N/A</div>
            </td>

            <td v-else-if="props.field === 'shipmentStatus'">
              <template v-for="shipmentStatus in props.dataItem['shipmentStatus']">
                <div :key="shipmentStatus.shipmentStatus.shipmentStatusEnum">
                  <div class="badge" :class="shipmentStatus.shipmentStatus.cssClass">
                    {{ shipmentStatus.count }} x {{ shipmentStatus.shipmentStatus.name }}
                  </div>
                </div>
              </template>
              <div v-if="!props.dataItem['shipmentStatus'].length">N/A</div>
            </td>

            <td v-else-if="props.field === 'times'">
              <div v-if="props.dataItem['minAvailable']">
                <span class="badge badge-secondary">AVA</span>
                {{ props.dataItem['minAvailable'] | formatDate }}
              </div>

              <div v-if="props.dataItem['minEstimated']">
                <span class="badge badge-primary">ETA</span>
                {{ props.dataItem['minEstimated'] | formatDate }}
              </div>

              <div v-if="props.dataItem['minActual']">
                <span class="badge badge-success">ATA</span>
                {{ props.dataItem['minActual'] | formatDate }}
              </div>
            </td>

            <td v-else-if="props.field === 'dateCalc'">
              <i class="far fa-clock"></i>
              {{ props.dataItem[props.field] | formatDate }}
            </td>
            <td v-else-if="props.field === 'stdCalc' || props.field === 'staCalc'">
              {{ props.dataItem[props.field] | formatDate('HH:mm') }}
            </td>
            <td v-else-if="props.field === 'action' && !materialRequest.isCompleted">
              <div class="text-nowrap text-right">
                <button class="badgebutton active ml-2" @click.stop="editPart(props.dataItem['id'])">
                  <i class="far fa-pencil text-white" />
                </button>
                <button class="badgebutton active danger ml-2" @click="openRemovePartModal(props.dataItem['id'])">
                  <i class="far fa-trash text-white" />
                </button>
              </div>
            </td>
            <td v-else>
              {{ props.dataItem[props.field] }}
            </td>
          </template>
        </grid>
      </div>

      <div class="col-12 mt-2 d-flex">
        <button class="badgebutton active" @click="addPart">
          <i class="far fa-plus"></i>
          Add Part
        </button>
        <button class="badgebutton ml-2" :class="{ active: canAddVendor }" :disabled="!canAddVendor" @click="openSelectVendors">
          <i class="far fa-plus"></i>
          Select vendors for selected parts
        </button>
        <button class="badgebutton ml-2" :class="{ active: canAddVendor }" :disabled="!canAddVendor" @click="openAddVendor">
          <i class="far fa-plus"></i>
          Add vendor for selected parts
        </button>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12">
        <div class="label mt-2 mb-1 d-flex justify-content-between align-items-center">
          <div>Vendor / Order Overview</div>
          <div>
            <button
              class="badgebutton active"
              @click="sendAllRequests"
              :disabled="numberOfPendingVendorRequests == 0 || sendingVendorRequests"
            >
              <i class="far fa-envelope mr-2"></i>
              <span v-if="numberOfPendingVendorRequests != 0">Send all pending requests ({{ numberOfPendingVendorRequests }})</span>
              <span v-else>No pending requests</span>
            </button>
          </div>
        </div>

        <div class="alert alert-info" v-if="!vendorRequests.length">
          <i class="far fa-info-circle"></i>
          No vendors are selected, please select the parts you want to request and click on
          <i class="far fa-plus"></i>
          Add Vendor
        </div>

        <div v-for="vendorRequest in vendorRequests" :key="vendorRequest.id">
          <VendorRequest :vendor-request="vendorRequest" @reload="loadData"></VendorRequest>
        </div>
      </div>
    </div>

    <b-modal
      centered
      ref="addVendor"
      id="addVendor"
      body-class="modal-body-wo"
      hide-footer
      hide-header
      size="widse"
      title="Add Vendor"
      :no-enforce-focus="true"
      :no-close-on-backdrop="true"
    >
      <AddVendor @cancel="closeAddVendor" @addvendor="addVendor"></AddVendor>
    </b-modal>

    <b-modal
      centered
      ref="selectVendors"
      id="selectVendors"
      body-class="modal-body-wo"
      hide-footer
      hide-header
      size="wide"
      title="Select Vendors"
      :no-enforce-focus="true"
      :no-close-on-backdrop="true"
    >
      <SelectVendor
        :selected-vendors="selectedVendors"
        :material-request-id="id"
        @cancel="closeSelectVendors"
        @selectvendors="selectVendors"
      ></SelectVendor>
    </b-modal>

    <b-modal id="removePartModal" ref="removePartModal" hide-footer hide-header body-class="modal-body-wo" centered>
      <modal-window :main-width="12">
        <div class="d-block">
          <h1>
            <i class="far fa-trash"></i>
            Remove Part
          </h1>
          <p>Do you really want to remove this part? The part will also be removed from all vendor requests.</p>
        </div>
        <hr class="mt-3" />
        <div class="mt-3 d-flex justify-content-end">
          <button class="mr-2 btn btn-secondary btn-sm" @click="cancelRemovePart">Cancel</button>
          <button class="btn btn-danger btn-sm" @click="removePart">Remove part</button>
        </div>
      </modal-window>
    </b-modal>

    <b-modal
      :ref="'managementEmail'"
      :id="'managementEmail'"
      centered
      body-class="modal-bodyas-wo"
      size="wide"
      title="E-Mail to Management"
      :no-enforce-focus="true"
      :no-close-on-backdrop="true"
      scrollable
    >
      <div v-if="reportIsSending" class="loading-spinner">
        <loading-spinner />
      </div>

      <VendorRequestEmail ref="emailText" :type="'management'" :vendor-request-id="materialRequest.id"></VendorRequestEmail>

      <hr class="mt-3" />

      <template v-slot:modal-footer>
        <div class="mt-3 d-flex justify-content-end">
          <button :disabled="reportIsSending" class="mr-2 btn btn-secondary btn-sm" @click="closeEmailModal">
            <i class="far fa-times"></i>
            Cancel
          </button>
          <button :disabled="reportIsSending" class="btn btn-primary btn-sm" @click="sendManagementStatusMail">
            <i class="far fa-envelope"></i>
            Send E-Mail
          </button>
        </div>
      </template>
    </b-modal>

    <router-view></router-view>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import {
    AddMaterialRequestVendorDto,
    AirportDto,
    MaterialRequestDto,
    PartOverviewViewDto,
    SelectMaterialRequestVendorDto,
    SpecialOccurrenceAccessLevel,
    VendorDto,
    VendorRequestDto,
    ManagementReportPostponeDto,
  } from '@/apiclient/apiclient_generated';
  import { Prop } from 'vue-property-decorator';
  import { GlobalHelper } from '@/helpers/global-helper';
  import TechnicalOccurrenceFlightsSelector from '@/components/SpecialOccurrence/Data/Flights/TechnicalOccurrenceFlightsSelector.vue';
  import { GridColumnProps } from '@progress/kendo-vue-grid';
  import { materialRequestClient } from '@/apiclient/opsboardapi';
  import AddVendor from '@/components/SpecialOccurrence/MaterialRequest/AddVendor.vue';
  import VendorRequestEmail from '@/components/SpecialOccurrence/MaterialRequest/VendorRequestEmail.vue';
  import VendorRequest from '@/components/SpecialOccurrence/MaterialRequest/VendorRequest.vue';
  import SelectVendor from '@/components/SpecialOccurrence/MaterialRequest/SelectVendor.vue';
  import ModalWindow from '@/components/ModalWindow.vue';
  import formatTime from '@/filters/formatTime';
  import formatDateTime from '@/filters/formatDateTime';
  import { MaterialRequestModule, MaterialRequestStore } from '@/store/materialRequest';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';

  @Component({
    components: {
      ModalWindow,
      SelectVendor,
      VendorRequest,
      VendorRequestEmail,
      AddVendor,
      TechnicalOccurrenceFlightsSelector,
      LoadingSpinner,
    },
  })
  export default class MaterialRequestData extends Vue {
    private store: SpecialOccurrenceModule = SpecialOccurrenceStore;
    private mrStore: MaterialRequestModule = MaterialRequestStore;
    @Prop() id: string;

    parts: PartOverviewViewDto[] = [];
    vendorRequests: VendorRequestDto[] = [];

    reportIsSending = false;
    private defaultPostponeValue = 1;
    postponeValue = this.defaultPostponeValue;

    selectedParts: string[] = [];
    numberOfPendingVendorRequests = 0;
    sendingVendorRequests = false;

    async mounted() {
      await this.loadData();
    }

    destroyed() {
      this.mrStore.setMaterialRequest({} as MaterialRequestDto);
    }

    get materialRequest() {
      return this.mrStore.materialRequest;
    }

    async loadData() {
      await this.mrStore.getMaterialRequest(this.id);

      this.parts = await materialRequestClient.getParts(this.id);
      this.vendorRequests = await materialRequestClient.getVendorRequests(this.id);
      this.numberOfPendingVendorRequests = await materialRequestClient.getNumberOfPendingVendorRequests(this.id);
    }

    get columns(): GridColumnProps[] {
      return [
        { field: 'selected', width: '50px', title: ' ' },
        { field: 'pn', title: 'PN', filterable: false, width: 100 },
        { field: 'ipc', title: 'IPC', filterable: false, width: 100 },
        { field: 'description', title: 'Description', filterable: false, width: 170 },
        { field: 'quantityRequested', title: 'Quantity', filterable: false, width: 70 },
        { field: 'altparts', title: 'Alt Parts Checked', filterable: false, width: 100 },
        { field: 'requestStatus', title: 'Request Status', filterable: false },
        { field: 'orderStatus', title: 'Order Status', filterable: false },
        { field: 'shipmentStatus', title: 'Shipment Status', filterable: false },
        { field: 'times', title: 'Time', filterable: false },
        { field: 'action', title: ' ', filterable: false, width: 80 },
      ];
    }

    get result() {
      return this.parts;
    }

    get selectedVendors(): string[] {
      return this.vendorRequests.map((_) => _.vendor.id);
    }

    get canAddVendor(): boolean {
      return this.selectedParts.length > 0;
    }

    get aircrafts() {
      return this.store.aircrafts;
    }

    get airports() {
      return this.store.airports;
    }

    get userCanEdit(): boolean {
      return (
        this.store.currentOccurrenceUserAccessLevel == SpecialOccurrenceAccessLevel.Admin ||
        this.store.currentOccurrenceUserAccessLevel == SpecialOccurrenceAccessLevel.Editor
      );
    }

    get stationInfo(): AirportDto {
      return this.store.airports.find((_) => _.id == this.materialRequest.airport) || new AirportDto();
    }

    toggleSelection(guid: string) {
      if (this.selectedParts.indexOf(guid) === -1) {
        this.selectedParts.push(guid);
      } else {
        this.selectedParts = this.selectedParts.filter((_) => _ != guid);
      }
    }

    editPart(partId: string) {
      this.$router.push({ name: 'MaterialRequestPartEdit', params: { materialRequestId: this.materialRequest.id, partId: partId } });
    }

    addPart() {
      this.$router.push({ name: 'MaterialRequestPartEdit', params: { materialRequestId: this.materialRequest.id, partId: '0' } });
    }

    openAddVendor() {
      const modal: any = this.$refs['addVendor'];
      modal.show();
    }

    closeAddVendor() {
      const modal: any = this.$refs['addVendor'];
      modal.hide();
    }

    async addVendor(vendor: VendorDto) {
      const dto = AddMaterialRequestVendorDto.fromJS({});
      dto.name = vendor.name;
      dto.email = vendor.email;
      dto.partIds = this.selectedParts;
      dto.materialRequestId = this.materialRequest.id;
      await materialRequestClient.addVendor(dto);
      await this.loadData();
      this.selectedParts.splice(0);
      this.closeAddVendor();
      GlobalHelper.NotifySuccess('Vendor added');
    }

    openSelectVendors() {
      const modal: any = this.$refs['selectVendors'];
      modal.show();
    }

    closeSelectVendors() {
      const modal: any = this.$refs['selectVendors'];
      modal.hide();
    }

    async selectVendors(vendors: string[]) {
      const dto = SelectMaterialRequestVendorDto.fromJS({});
      dto.vendorIds = vendors;
      dto.partIds = this.selectedParts;
      dto.materialRequestId = this.materialRequest.id;
      await materialRequestClient.selectVendors(dto);
      await this.loadData();
      this.selectedParts.splice(0);
      this.closeSelectVendors();
      GlobalHelper.NotifySuccess('Vendors selected');
    }

    async saveChanges() {
      GlobalHelper.NotifySuccess('Material Request updated');
      await materialRequestClient.saveChanges(this.materialRequest);
      await this.loadData();
    }

    partToBeRemovedId = '';
    openRemovePartModal(id: string) {
      this.partToBeRemovedId = id;
      const modal: any = this.$refs['removePartModal'];
      modal.show();
    }

    cancelRemovePart() {
      const modal: any = this.$refs['removePartModal'];
      modal.hide();
      this.partToBeRemovedId = '';
    }

    async removePart() {
      if (this.partToBeRemovedId) {
        await materialRequestClient.deletePart(this.partToBeRemovedId);
        this.partToBeRemovedId = '';
        const modal: any = this.$refs['removePartModal'];
        modal.hide();

        await this.loadData();
      }
    }

    async processMaterialRequestReport() {
      try {
        const response = await materialRequestClient.processMaterialRequestReport(this.materialRequest.id);
        if (response.isSuccess) {
          GlobalHelper.NotifySuccess(response.message);
        } else {
          GlobalHelper.NotifyError(response.message);
        }
      } catch {
        GlobalHelper.NotifyError('Something went wrong');
      }
    }

    async sendAllRequests() {
      try {
        this.sendingVendorRequests = true;
        const count = await materialRequestClient.sendAllPendingVendorRequests(this.materialRequest.id);
        await this.loadData();
        GlobalHelper.NotifySuccess('E-Mail was sent to ' + count + ' contacts');
      } catch (e) {
        GlobalHelper.NotifyError('Error sending E-Mail');
      } finally {
        this.sendingVendorRequests = false;
      }
    }

    navigateTechnicalOccurrence() {
      this.$router.push({ name: 'MaterialRequestLinkIntermediate', params: { id: this.materialRequest.technicalOccurrenceGuid } });
    }

    previewEmail() {
      const modal: any = this.$refs['managementEmail'];
      modal.show();
    }

    async sendManagementStatusMail() {
      try {
        this.reportIsSending = true;
        await materialRequestClient.sendManagementStatusEmail(this.materialRequest.id);
        GlobalHelper.NotifySuccess('E-Mail was sent');
        this.closeEmailModal();
      } catch (e) {
        GlobalHelper.NotifyError('Error sending E-Mail');
      } finally {
        this.reportIsSending = false;
      }
    }

    closeEmailModal() {
      const modal: any = this.$refs['managementEmail'];
      modal.hide();
    }

    cancelReportPostpone() {
      const popover: any = this.$refs.reportPostponePopover;
      if (popover) {
        popover.doClose();
        this.postponeValue = this.defaultPostponeValue;
      }
    }

    async saveReportPostpone() {
      const command = {
        materialRequestId: this.materialRequest.id,
        postponeHours: this.postponeValue,
      } as ManagementReportPostponeDto;

      await materialRequestClient.postponeManagementReport(command);

      GlobalHelper.NotifySuccess('Next report send was successfully postponed.');

      const popover: any = this.$refs.reportPostponePopover;
      if (popover) {
        popover.doClose();
        this.postponeValue = this.defaultPostponeValue;
      }
    }

    getFormattedTimeValue(timeValue) {
      if (!timeValue) {
        return ' -- : -- ';
      }

      // return formatTime(timeValue);
      return formatDateTime(timeValue);
    }
  }
</script>

<style lang="scss" scoped>
  .selected {
    //background-color: yellow;
  }

  .k-grid td {
    border-width: 0 !important;
  }

  .mr-header {
    align-items: center;

    .report-schedule-info {
      display: grid;
      grid-template-columns: 38% 42% 16%;
      justify-content: flex-start;
      align-items: center;
      gap: 4px;
    }
  }

  .popover {
    .postpone-header {
      font-weight: bold;
      font-size: 1rem;
      color: black !important;

      text-align: center;
    }

    .postpone-body {
      display: grid;
      grid-template-columns: 35% 50% 15%;
      justify-content: flex-start;
      align-items: center;
      gap: 10px;
    }

    .postpone-actions {
      display: grid;
      grid-template-columns: 1fr 1fr;
      justify-content: flex-end;
      align-items: center;
      gap: 10px;
    }
  }

  .loading-spinner {
    position: absolute;
    top: 50%;
    left: 50%;
    margin-right: -50%;
    transform: translate(-50%, -50%);
  }
</style>
